import React, {
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import { MESSAGE_TYPES, sendUpdateToIframe } from "../../../utils/editIframe";
import StatusMessage from "../../../components/common/StatusMessage";
import Iframe from "../../../components/Iframe";
import {
  clearValidationErrors,
  executeValidation,
} from "../../../utils/formValidation";

const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "News", path: `/news` },
];

function NewsEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const { newsId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const contentKey = "newsContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const [selectedNewsId, setSelectedNewsId] = useState(null);
  const [isEditingNew, setIsEditingNew] = useState(false);
  const [currentFormData, setCurrentFormData] = useState(null);
  const formRef = useRef(null);

  // コールバック関数を安定化
  const handleFormDataChange = useCallback(
    (formData) => {
      // formDataが null/undefined の場合は何もしない
      if (!formData) {
        return;
      }

      // 記事選択直後に空のデータが送られてくる場合の処理
      // 新規作成時（isEditingNew=true）の場合は空データも受け入れる
      // 既存記事選択時（currentFormDataに有効なIDがある）で空データが来た場合は無視
      if (!formData.id && formData.title === "" && formData.content === "") {
        if (!isEditingNew && currentFormData && currentFormData.id) {
          return;
        }
      }

      // レンダリング中の state 更新を避けるため、次のフレームで実行
      setTimeout(() => {
        setCurrentFormData(formData);
      }, 0);
    },
    [currentFormData, isEditingNew]
  );
  const [pageContent, setPageContent] = useState({
    news_items: [],
  });

  // 新規作成時の固定オブジェクト（useMemoで安定化）
  const newNewsTemplate = useMemo(
    () => ({
      id: null,
      title: "",
      content: "",
      is_published: false,
      published_at: null,
      image: {},
    }),
    []
  );

  // データ取得処理
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_news_content/`);
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      setPageContent(data);
    } catch (error) {
      console.error("データ取得エラー:", error);
      // エラー時は空のデータを設定
      setPageContent({
        news_items: [],
      });
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData();
    fetchMediaFiles();
  }, [fetchPageContentData, fetchMediaFiles]);

  // URL parameter handling
  useEffect(() => {
    if (newsId === "new") {
      // 新規作成モード
      if (!isEditingNew) {
        setIsEditingNew(true);
        setSelectedNewsId(null);
        setCurrentFormData(newNewsTemplate);

        // フォーム初期化を次のフレームで実行
        setTimeout(() => {
          formRef.current?.initializeForm(newNewsTemplate, true);
        }, 0);
      }
    } else if (newsId && !isNaN(parseInt(newsId))) {
      // 記事編集モード
      const targetNewsId = parseInt(newsId);
      if (targetNewsId !== selectedNewsId) {
        setSelectedNewsId(targetNewsId);
        setIsEditingNew(false);
      }
    } else if (!newsId) {
      // 一覧表示モード
      if (selectedNewsId !== null || isEditingNew) {
        setSelectedNewsId(null);
        setIsEditingNew(false);
        setCurrentFormData(null);
      }
    }
  }, [newsId, selectedNewsId, isEditingNew, newNewsTemplate]);

  // selectedNewsIdが変更された時に記事データを設定
  useEffect(() => {
    if (selectedNewsId && !isEditingNew && pageContent.news_items.length > 0) {
      const selectedNews = pageContent.news_items.find(
        (item) => item.id === selectedNewsId
      );

      if (selectedNews) {
        setCurrentFormData(selectedNews);

        // フォーム初期化とiframe通信を次のフレームで実行
        setTimeout(() => {
          formRef.current?.initializeForm(selectedNews, false);

          // 少し遅延してiframeにも送信（フォーム初期化完了後）
          setTimeout(() => {
            sendUpdateToIframe(
              MESSAGE_TYPES.TEXT_UPDATE,
              "title",
              selectedNews.title || "",
              "newsContent"
            );
            sendUpdateToIframe(
              MESSAGE_TYPES.TEXT_UPDATE,
              "content",
              selectedNews.content || "",
              "newsContent"
            );
            sendUpdateToIframe(
              MESSAGE_TYPES.IMAGE_UPDATE,
              "image",
              selectedNews.image || null,
              "newsContent"
            );
          }, 100);
        }, 0);
      }
    }
  }, [selectedNewsId, isEditingNew, pageContent.news_items]);

  // 記事一覧の再読み込み
  const handleReset = async () => {
    clearValidationErrors();
    setMessage("");
    await fetchPageContentData();
    setMessage("記事一覧を更新しました");
    setTimeout(() => setMessage(""), 3000);
  };

  // 現在選択中の記事を保存
  const handleSubmit = async () => {
    if (!selectedNews && !isEditingNew) {
      setMessage("保存する記事が選択されていません");
      setTimeout(() => setMessage(""), 3000);
      return;
    }

    if (!currentFormData) {
      setMessage("フォームデータが取得できません");
      setTimeout(() => setMessage(""), 3000);
      return;
    }

    const validationRules = {
      title: { required: true },
      published_at: { required: true },
      content: { required: true },
      image: { required: true },
    };

    const fieldLabels = {
      title: "記事タイトル",
      published_at: "公開日",
      content: "記事内容",
      image: "記事画像",
    };

    if (
      !executeValidation(
        currentFormData,
        validationRules,
        fieldLabels,
        setMessage
      )
    ) {
      return;
    }

    // 保存時にコンテンツをクリーンアップ（API送信用）
    const currentNewsData = {
      ...currentFormData,
      content:
        typeof currentFormData.content === "string"
          ? cleanHtmlContent(currentFormData.content)
          : currentFormData.content,
    };

    try {
      setLoadingText("保存中");
      const apiUrl = process.env.REACT_APP_API_URL;

      const requestData = {
        news_data: {
          ...currentNewsData,
          id: isEditingNew ? null : selectedNewsId,
        },
      };

      const response = await fetch(`${apiUrl}/api/update_page_news_content/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(requestData),
      });

      if (!response.ok) throw new Error("保存に失敗しました");

      const result = await response.json();

      // データを再取得して画面を更新
      await fetchPageContentData();

      if (isEditingNew) {
        // 新規作成後は編集モードのURLに変更
        navigate(`/news/${result.id}`, { replace: true });
      }

      setMessage("記事を保存しました");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("保存エラー:", error);
      setMessage("保存に失敗しました");
      setTimeout(() => setMessage(""), 3000);
    }
    setLoadingText("");
  };

  // 記事選択処理（直接ナビゲート）
  const handleSelectNews = useCallback(
    (newsId) => {
      const targetPath = `/news/${newsId}`;
      navigate(targetPath, { replace: true });
    },
    [navigate]
  );

  // 新規記事作成（直接ナビゲート）
  const handleCreateNew = useCallback(() => {
    const targetPath = "/news/new";
    navigate(targetPath, { replace: true });
  }, [navigate]);

  // 記事削除処理（論理削除）
  const handleDeleteNews = async (newsId) => {
    if (!newsId) {
      setMessage("削除する記事が選択されていません");
      setTimeout(() => setMessage(""), 3000);
      return;
    }

    if (!window.confirm("この記事を削除しますか？")) {
      return;
    }

    try {
      setLoadingText("削除中");
      const apiUrl = process.env.REACT_APP_API_URL;

      // delete_flgをtrueにする論理削除の処理
      const requestData = {
        news_data: {
          id: newsId,
          delete_flg: true,
        },
      };

      const response = await fetch(`${apiUrl}/api/update_page_news_content/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "削除に失敗しました");
      }

      // 削除した記事が選択中だった場合、選択を解除（データ再取得前に実行）
      if (selectedNewsId === newsId) {
        setSelectedNewsId(null);
        setIsEditingNew(false);
        setCurrentFormData(null);
        // URLも一覧に戻す（現在のURLと異なる場合のみ）
        const currentPath = location.pathname;
        const targetPath = "/news";
        if (currentPath !== targetPath) {
          navigate(targetPath, { replace: true });
        }
      }

      // データを再取得して画面を更新
      try {
        await fetchPageContentData();
      } catch (fetchError) {
        console.warn(
          "データ再取得でエラーが発生しましたが、削除は成功しました:",
          fetchError
        );
      }

      setMessage("記事を削除しました");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("削除エラー:", error);
      setMessage(`削除に失敗しました: ${error.message}`);
      setTimeout(() => setMessage(""), 3000);
    }
    setLoadingText("");
  };

  // ファイルドロップ処理
  const handleDrop = useCallback(
    async (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }
      setLoadingText("アップロード中");

      // ファイルを取得（input change event or drag & drop event）
      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];

      if (!file) {
        console.warn("No file found in event");
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        await fetchMediaFiles();
        if (data.images?.[0]) {
          selectMedia(data.images[0]);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
        alert("アップロードに失敗しました");
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia]
  );

  // MediaGalleryからの画像選択を処理
  const handleMediaGallerySelect = (media) => {
    // 現在編集中のフォームがある場合のみ処理
    if (selectedNews || isEditingNew) {
      setTimeout(() => {
        sendUpdateToIframe(
          MESSAGE_TYPES.IMAGE_UPDATE,
          "image",
          media,
          "newsContent"
        );
      }, 50);
    }
  };

  // 選択中の記事データを安定化
  const selectedNews = useMemo(() => {
    if (isEditingNew) {
      return newNewsTemplate;
    }

    const foundNews =
      pageContent.news_items.find((item) => item.id === selectedNewsId) || null;
    return foundNews;
  }, [isEditingNew, newNewsTemplate, pageContent.news_items, selectedNewsId]);

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
              newsTitle={
                currentFormData?.title || (isEditingNew ? "新規記事" : null)
              }
            />
            <StatusMessage message={message} type="success" />

            <div className="form">
              {/* 記事一覧セクション */}
              <section>
                <div className="area4">
                  <div className="item">
                    <p>記事一覧 ({pageContent.news_items.length}件)</p>
                    {pageContent.news_items.length === 0 ? (
                      <p className="no-news">
                        記事がありません。新規記事を作成してください。
                      </p>
                    ) : (
                      <div className="news-grid">
                        <div className="news-card" onClick={handleCreateNew}>
                          <div className="news-card-content">
                            <p className="news-title add">Add New Post</p>
                          </div>
                        </div>
                        {pageContent.news_items.map((news, index) => (
                          <div
                            key={news.id}
                            className={`news-card ${
                              selectedNewsId === news.id ? "selected" : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectNews(news.id);
                            }}
                          >
                            <div className="news-card-content">
                              <p className="news-date">
                                {news.published_at && news.published_at}
                              </p>
                              <p className="news-title">
                                {news.title || `記事${index + 1}`}
                              </p>
                              <p>{news.is_published ? "公開中" : "下書き"}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* 記事編集フォーム */}
              {(selectedNews || isEditingNew) && (
                <section>
                  <div className="area3">
                    <div className="item">
                      <MemoizedNewsEditForm
                        ref={formRef}
                        news={selectedNews}
                        onFormDataChange={handleFormDataChange}
                        isNew={isEditingNew}
                        onDelete={handleDeleteNews}
                      />
                    </div>
                  </div>
                </section>
              )}
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handleSubmit}>
                Submit
              </button>
            </div>

            {isMediaOpen && (
              <MediaGallery
                onUpload={handleDrop}
                onSelect={handleMediaGallerySelect}
              />
            )}
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe
        path={
          selectedNewsId
            ? `/news/${selectedNewsId}`
            : isEditingNew
            ? "/news/new"
            : "/archive"
        }
        data={pageContent}
        contentKey={contentKey}
      />
    </>
  );
}

// HTMLコンテンツをクリーンアップする関数（共通関数として定義）
const cleanHtmlContent = (html) => {
  if (!html) return html;

  let cleaned = html;

  // 画像が含まれている場合は、SunEditorのコンテナをシンプルなimgタグに変換
  if (html.includes("se-image-container")) {
    // se-image-containerからimgタグを抽出してシンプルなpタグに変換
    cleaned = cleaned.replace(
      /<div[^>]*se-image-container[^>]*>.*?<img([^>]*?)>.*?<\/div>/gs,
      (match, imgAttrs) => {
        // 基本的な属性のみを抽出（src, alt, style, width, height）
        const srcMatch = imgAttrs.match(/src="([^"]*?)"/);
        const altMatch = imgAttrs.match(/alt="([^"]*?)"/);
        const styleMatch = imgAttrs.match(/style="([^"]*?)"/);
        const widthMatch = imgAttrs.match(/width="([^"]*?)"/);
        const heightMatch = imgAttrs.match(/height="([^"]*?)"/);

        let cleanAttrs = "";
        if (srcMatch) cleanAttrs += ` src="${srcMatch[1]}"`;
        if (altMatch) cleanAttrs += ` alt="${altMatch[1]}"`;
        if (styleMatch) cleanAttrs += ` style="${styleMatch[1]}"`;
        if (widthMatch) cleanAttrs += ` width="${widthMatch[1]}"`;
        if (heightMatch) cleanAttrs += ` height="${heightMatch[1]}"`;

        const result = `<p><img${cleanAttrs}></p>`;
        return result;
      }
    );
  } else if (html.includes("<img")) {
    console.log("cleanHtmlContent - found direct img tags, keeping as-is");
    // 直接imgタグが含まれている場合はそのまま保持
  } else {
    // 画像がない場合は従来通りse-componentを除去
    cleaned = cleaned.replace(/<div[^>]*se-component[^>]*>.*?<\/div>/gs, "");
  }

  // 空のspanタグのみを除去（style属性があるものは残す）
  cleaned = cleaned.replace(
    /<span[^>]*(?<!style="[^"]*")[^>]*>\s*<\/span>/g,
    ""
  );

  // 連続する<br>タグを制限（4個以上は2個に）
  cleaned = cleaned.replace(/(<br\s*\/?>\s*){4,}/g, "<br><br>");

  // 余分な空白を除去
  cleaned = cleaned.replace(/\n\s*\n/g, "\n");
  cleaned = cleaned.trim();

  return cleaned;
};

// ニュース編集フォームコンポーネント
const NewsEditForm = forwardRef(
  ({ onFormDataChange, isNew, onDelete }, ref) => {
    const { openMedia, registerSetter } = useMedia();
    const [formData, setFormData] = useState({
      id: null,
      title: "",
      content: "",
      is_published: false,
      published_at: null,
      image: {},
    });
    const editorRef = useRef(null);
    const [editorInstance, setEditorInstance] = useState(null);
    const [isInsertingImage, setIsInsertingImage] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);

    // SunEditorに画像を挿入する関数
    const handleEditorImageSelect = useCallback(
      (media) => {
        if (media?.image_url) {
          setIsInsertingImage(true);

          try {
            // SunEditorのインスタンスを取得
            let editor = editorInstance || null;

            // editorInstanceが無い場合はrefから取得を試す
            if (!editor && editorRef.current) {
              console.log(
                "Available methods in ref:",
                Object.keys(editorRef.current)
              );

              if (editorRef.current.getInstance) {
                editor = editorRef.current.getInstance();
                console.log("Got editor via getInstance():", !!editor);
              } else if (editorRef.current.editor) {
                editor = editorRef.current.editor;
                console.log("Got editor via .editor property:", !!editor);
              } else if (editorRef.current.getEditor) {
                editor = editorRef.current.getEditor();
                console.log("Got editor via getEditor():", !!editor);
              }
            }

            if (editor) {
              // 画像HTMLを作成
              const imgHtml = `<p><img src="${media.image_url}" alt="${
                media.alt || "画像"
              }" style="max-width: 100%; height: auto;" /></p>`;

              // エディタにフォーカスを当てる
              try {
                if (typeof editor.focus === "function") {
                  editor.focus();
                } else if (
                  editor.core &&
                  typeof editor.core.focus === "function"
                ) {
                  editor.core.focus();
                } else if (
                  editor.wysiwyg &&
                  typeof editor.wysiwyg.focus === "function"
                ) {
                  editor.wysiwyg.focus();
                }
              } catch (focusError) {
                // フォーカス失敗は無視
              }

              // 画像を挿入
              if (typeof editor.insertHTML === "function") {
                editor.insertHTML(imgHtml);
              } else if (typeof editor.insertImage === "function") {
                editor.insertImage(media.image_url, media.alt || "画像");
              } else if (
                editor.core &&
                typeof editor.core.insertHTML === "function"
              ) {
                editor.core.insertHTML(imgHtml);
              } else {
                document.execCommand("insertHTML", false, imgHtml);
              }

              // 挿入後のコンテンツを取得して状態を更新
              setTimeout(() => {
                let newContent = "";
                if (typeof editor.getContents === "function") {
                  newContent = editor.getContents();
                } else if (typeof editor.getHTML === "function") {
                  newContent = editor.getHTML();
                } else if (editor.innerHTML) {
                  newContent = editor.innerHTML;
                } else {
                  setIsInsertingImage(false);
                  return;
                }

                // formDataには生のコンテンツを保存（エディタ表示用）
                setFormData((prev) => {
                  const newFormData = { ...prev, content: newContent };

                  // 親コンポーネントに通知
                  setTimeout(() => onFormDataChange?.(newFormData), 0);

                  return newFormData;
                });

                // iframeには清理したコンテンツを送信
                const cleanedContent = cleanHtmlContent(newContent);
                setTimeout(() => {
                  console.log(
                    "Image insertion - sending cleaned content to iframe:",
                    {
                      type: MESSAGE_TYPES.TEXT_UPDATE,
                      fieldName: "content",
                      originalLength: newContent?.length,
                      cleanedLength: cleanedContent?.length,
                      containsImg: cleanedContent?.includes("<img"),
                    }
                  );
                  sendUpdateToIframe(
                    MESSAGE_TYPES.TEXT_UPDATE,
                    "content",
                    cleanedContent,
                    "newsContent"
                  );
                }, 50);

                setIsInsertingImage(false);
              }, 100);
            } else {
              // 改善されたフォールバック：エディタの内容を強制更新
              const imgHtml = `<p><img src="${media.image_url}" alt="${
                media.alt || "画像"
              }" style="max-width: 100%; height: auto;" /></p>`;

              setFormData((prev) => {
                const newContent = (prev.content || "") + imgHtml;
                const newFormData = { ...prev, content: newContent };

                setTimeout(() => onFormDataChange?.(newFormData), 0);

                // エディタの内容も強制的に更新（生のコンテンツを使用）
                setTimeout(() => {
                  const editorInst =
                    editorInstance ||
                    (editorRef.current?.getInstance
                      ? editorRef.current.getInstance()
                      : null);
                  if (editorInst) {
                    if (typeof editorInst.setContents === "function") {
                      editorInst.setContents(newContent);
                      console.log(
                        "Forced editor content update via setContents"
                      );
                    } else if (typeof editorInst.setHTML === "function") {
                      editorInst.setHTML(newContent);
                      console.log("Forced editor content update via setHTML");
                    } else {
                      console.log(
                        "No suitable forced content update method found"
                      );
                    }
                  }
                }, 200);

                // iframeにはクリーンアップされたコンテンツを送信
                const cleanedContent = cleanHtmlContent(newContent);
                setTimeout(() => {
                  console.log("Fallback - sending cleaned content to iframe:", {
                    type: MESSAGE_TYPES.TEXT_UPDATE,
                    fieldName: "content",
                    containsImg: cleanedContent.includes("<img"),
                  });
                  sendUpdateToIframe(
                    MESSAGE_TYPES.TEXT_UPDATE,
                    "content",
                    cleanedContent,
                    "newsContent"
                  );
                }, 250);

                return newFormData;
              });
              setIsInsertingImage(false);
            }
          } catch (error) {
            console.error("Error inserting image:", error);
            setIsInsertingImage(false);
          }
        }
      },
      [onFormDataChange, editorInstance]
    );

    // MediaSelectorを開く関数
    const openEditorMediaSelector = useCallback(() => {
      openMedia("editor_image", null, "image", "single");
    }, [openMedia]);

    // MediaSelector登録
    useEffect(() => {
      registerSetter("editor_image", handleEditorImageSelect, false);
    }, [registerSetter, handleEditorImageSelect]);

    // SunEditorのツールバーにカスタムボタンを追加
    useEffect(() => {
      if (editorInstance) {
        setTimeout(() => {
          console.log("Attempting to add custom button to toolbar");

          // ツールバーコンテナを取得
          const toolbarContainer = document.querySelector(".se-toolbar");
          console.log("Toolbar container:", toolbarContainer);

          if (
            toolbarContainer &&
            !document.querySelector(".custom-image-insert-btn")
          ) {
            // ツールバー内の構造を調査
            console.log("Toolbar HTML:", toolbarContainer.innerHTML);
            // 第1アプローチ: 最後の.se-menu-listにリストアイテムとして追加
            const menuLists =
              toolbarContainer.querySelectorAll(".se-menu-list");
            console.log("Menu lists found:", menuLists.length);
            if (menuLists.length > 0) {
              const lastMenuList = menuLists[menuLists.length - 1];
              console.log("Using last menu list as insertion point");

              // リストアイテムとしてボタンを作成
              const listItem = document.createElement("li");
              const customButton = document.createElement("button");
              customButton.className =
                "se-btn se-tooltip custom-image-insert-btn";
              customButton.type = "button";
              customButton.title = "画像を挿入";
              customButton.setAttribute("data-command", "customImageInsert");
              customButton.setAttribute("data-display", "");
              customButton.setAttribute("aria-label", "画像を挿入");
              customButton.setAttribute("tabindex", "-1");
              customButton.innerHTML = `
                <svg viewBox="0 0 24 24" style="width: 16px; height: 16px; fill: currentColor;">
                  <path d="M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z"/>
                </svg>
                <span class="se-tooltip-inner"><span class="se-tooltip-text">画像を挿入</span></span>
              `;

              // クリックイベントを追加
              customButton.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                openEditorMediaSelector();
              });

              listItem.appendChild(customButton);
              lastMenuList.appendChild(listItem);

              console.log("Custom button added to menu list successfully");
              return; // 成功したら終了
            }

            // 第2アプローチ: 既存の方法
            let insertionPoint = null;

            // 方法1: .se-btn-list クラスを探す
            const buttonGroups =
              toolbarContainer.querySelectorAll(".se-btn-list");
            console.log("Button groups found:", buttonGroups.length);
            if (buttonGroups.length > 0) {
              insertionPoint = buttonGroups[buttonGroups.length - 1];
              console.log("Using last button group as insertion point");
            } else {
              // 方法2: .se-btn クラスを探す
              const buttons = toolbarContainer.querySelectorAll(".se-btn");
              console.log("Individual buttons found:", buttons.length);

              if (buttons.length > 0) {
                insertionPoint = buttons[buttons.length - 1];
                console.log("Using last button as insertion point");
              } else {
                // 方法3: ツールバーの最後に追加
                insertionPoint = toolbarContainer;
                console.log("Using toolbar container as insertion point");
              }
            }

            // 第2アプローチが実行される場合
            if (insertionPoint) {
              // 新しいボタングループを作成
              const newButtonModule = document.createElement("div");
              newButtonModule.className = "se-btn-module se-btn-module-border";

              const newMenuList = document.createElement("ul");
              newMenuList.className = "se-menu-list";

              const listItem = document.createElement("li");

              const customButton = document.createElement("button");
              customButton.className =
                "se-btn se-tooltip custom-image-insert-btn";
              customButton.type = "button";
              customButton.title = "画像を挿入";
              customButton.setAttribute("data-command", "customImageInsert");
              customButton.setAttribute("data-display", "");
              customButton.setAttribute("aria-label", "画像を挿入");
              customButton.setAttribute("tabindex", "-1");
              customButton.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z"/>
                </svg>
                <span class="se-tooltip-inner"><span class="se-tooltip-text">画像を挿入</span></span>
              `;

              // クリックイベントを追加
              customButton.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log("Custom image button clicked (method 2)");
                openEditorMediaSelector();
              });

              // 構造を組み立て
              listItem.appendChild(customButton);
              newMenuList.appendChild(listItem);
              newButtonModule.appendChild(newMenuList);

              // セパレーターを作成
              const separator = document.createElement("div");
              separator.className = "se-toolbar-separator-vertical";

              // ツールバーの最後に追加
              const btnTray = toolbarContainer.querySelector(".se-btn-tray");
              if (btnTray) {
                btnTray.appendChild(separator);
                btnTray.appendChild(newButtonModule);
                console.log(
                  "Custom button module added to btn-tray successfully"
                );
              } else {
                toolbarContainer.appendChild(separator);
                toolbarContainer.appendChild(newButtonModule);
                console.log(
                  "Custom button module added to toolbar successfully"
                );
              }
            } else {
              console.error("Could not find insertion point for custom button");
            }
          } else {
            if (!toolbarContainer) {
              console.error("Toolbar container not found");
            }
            if (document.querySelector(".custom-image-insert-btn")) {
              console.log("Custom button already exists");
            }
          }
        }, 1500); // さらに遅延して確実に描画後に実行
      }
    }, [editorInstance, openEditorMediaSelector]);

    // 手動初期化関数
    const initializeForm = useCallback(
      (newsData, isNewFlag) => {
        if (newsData && !isNewFlag) {
          const newFormData = {
            id: newsData.id,
            title: newsData.title || "",
            content: newsData.content || "",
            is_published: newsData.is_published || false,
            published_at: newsData.published_at || null,
            image: newsData.image || {},
          };
          console.log(
            "NewsEditForm - Manual initialization with existing news:",
            newFormData
          );

          // 初期化フラグを設定
          setIsInitializing(true);

          // setFormDataを確実に実行
          setFormData((prevData) => {
            console.log("setFormData callback in initializeForm:", {
              prevData,
              newFormData,
            });
            return newFormData;
          });

          // formDataの更新完了を確実にするため、次のイベントループで実行
          setTimeout(() => {}, 10);

          // エディタの内容更新（遅延実行で確実に設定）
          setTimeout(() => {
            setIsInitializing(false);
          }, 100);

          // onFormDataChangeの呼び出しを一度だけ、遅延して実行
          setTimeout(() => {
            onFormDataChange?.(newFormData);
          }, 50);
        } else if (isNewFlag) {
          const newFormData = {
            id: null,
            title: "",
            content: "",
            is_published: false,
            published_at: null,
            image: {},
          };
          console.log(
            "NewsEditForm - Manual initialization for new news:",
            newFormData
          );

          // 初期化フラグを設定
          setIsInitializing(true);

          // setFormDataを確実に実行
          setFormData((prevData) => {
            return newFormData;
          });

          // エディタをクリア
          setTimeout(() => {
            const editor =
              editorInstance ||
              (editorRef.current?.getInstance
                ? editorRef.current.getInstance()
                : null);
            if (editor) {
              if (typeof editor.setContents === "function") {
                editor.setContents("");
                console.log("Editor cleared via initializeForm setContents");
              } else if (typeof editor.setHTML === "function") {
                editor.setHTML("");
                console.log("Editor cleared via initializeForm setHTML");
              } else {
                console.log("No suitable content clearing method found");
              }
            } else {
              console.log("No editor instance available for clearing");
            }

            // 初期化完了
            setTimeout(() => {
              setIsInitializing(false);
            }, 100);
          }, 200);

          // onFormDataChangeの呼び出しを一度だけ、遅延して実行
          setTimeout(() => {
            onFormDataChange?.(newFormData);
          }, 50);
        }
      },
      [onFormDataChange, editorInstance]
    );

    // 親から初期化を呼び出すためのrefを公開
    useImperativeHandle(
      ref,
      () => ({
        initializeForm,
      }),
      [initializeForm]
    );

    const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      let newValue = type === "checkbox" ? checked : value;

      console.log("handleInputChange called:", {
        name,
        value:
          typeof value === "string" ? value.substring(0, 100) + "..." : value,
        currentFormData: {
          id: formData.id,
          title: formData.title?.substring(0, 20) + "...",
          content: formData.content?.substring(0, 50) + "...",
          is_published: formData.is_published,
          image: formData.image,
        },
        isInitializing,
        isInsertingImage,
      });

      // 初期化中の変更は無視
      if (isInitializing) {
        console.log("Ignoring handleInputChange during initialization");
        return;
      }

      // 画像挿入中の不要な更新を無視
      if (isInsertingImage && name === "content") {
        return;
      }

      // contentフィールドのクリーンアップは行わない（エディタ内では生のHTMLを維持）
      // クリーンアップはiframe送信時とAPI送信時のみ実行

      // フォームデータの更新を安全に行う
      setFormData((prevFormData) => {
        console.log("setFormData callback - preserving existing data:", {
          prevId: prevFormData.id,
          prevTitle: prevFormData.title,
          prevIsPublished: prevFormData.is_published,
          prevImage: prevFormData.image,
          updatingField: name,
          newValue: name === "content" ? "content updated" : newValue,
        });

        const newFormData = {
          ...prevFormData, // 確実に既存データを保持
          [name]: newValue, // 指定フィールドのみ更新
        };

        // 親コンポーネントへの通知（重複呼び出しを防ぐ）
        if (
          name !== "content" ||
          Math.abs(
            newFormData.content?.length - (prevFormData.content?.length || 0)
          ) > 1
        ) {
          setTimeout(() => {
            onFormDataChange?.(newFormData);
          }, 0);
        }

        return newFormData;
      });

      // iframeにリアルタイムで反映
      setTimeout(() => {
        if (name === "title") {
          sendUpdateToIframe(
            MESSAGE_TYPES.TEXT_UPDATE,
            "title",
            newValue,
            "newsContent"
          );
        } else if (name === "content") {
          // iframe送信用にクリーンアップされたコンテンツを使用
          const cleanedForIframe =
            name === "content" && typeof newValue === "string"
              ? cleanHtmlContent(newValue)
              : newValue;
          sendUpdateToIframe(
            MESSAGE_TYPES.TEXT_UPDATE,
            "content",
            cleanedForIframe,
            "newsContent"
          );
        } else if (name === "published_at") {
          sendUpdateToIframe(
            MESSAGE_TYPES.TEXT_UPDATE,
            "published_at",
            newValue,
            "newsContent"
          );
        }
      }, 50);
    };

    const handleImageSelect = useCallback(
      (media) => {
        setFormData((currentFormData) => {
          const newFormData = {
            ...currentFormData,
            image: media,
          };
          // レンダリング中の呼び出しを避けるため非同期で実行
          setTimeout(() => {
            onFormDataChange?.(newFormData);
          }, 0);

          return newFormData;
        });

        // 画像変更をiframeに反映
        setTimeout(() => {
          sendUpdateToIframe(
            MESSAGE_TYPES.IMAGE_UPDATE,
            "image",
            media,
            "newsContent"
          );
        }, 50);
      },
      [onFormDataChange]
    );

    // formDataの変化をログ出力
    useEffect(() => {}, [formData]);

    // エディタインスタンスとformDataが準備できた時にコンテンツを設定
    useEffect(() => {
      if (
        editorInstance &&
        formData.content &&
        !isInitializing &&
        !isInsertingImage
      ) {
        console.log(
          "Editor instance and formData content both available, setting content:",
          formData.content.substring(0, 100)
        );

        setTimeout(() => {
          // エディタの現在のコンテンツと比較して、異なる場合のみ設定
          let currentContent = "";
          if (typeof editorInstance.getContents === "function") {
            currentContent = editorInstance.getContents();
          } else if (typeof editorInstance.getHTML === "function") {
            currentContent = editorInstance.getHTML();
          }

          if (currentContent !== formData.content) {
            if (typeof editorInstance.setContents === "function") {
              editorInstance.setContents(formData.content);
              console.log("Content synced to editor via setContents");
            } else if (typeof editorInstance.setHTML === "function") {
              editorInstance.setHTML(formData.content);
              console.log("Content synced to editor via setHTML");
            }
          } else {
            console.log(
              "Editor content already matches formData, no update needed"
            );
          }
        }, 50);
      }
    }, [editorInstance, formData.content, isInitializing, isInsertingImage]);

    // コンポーネントのクリーンアップ時にデバウンスタイマーをクリア
    useEffect(() => {
      return () => {
        clearTimeout(window.inputDebounceTimer);
        clearTimeout(window.keyupDebounceTimer);
        clearTimeout(window.saveDebounceTimer);
      };
    }, []);

    return (
      <div className="news-edit-form">
        <div className="forms">
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="記事タイトル"
            className="input1"
            data-label="記事タイトル"
            data-required="true"
          />

          <input
            type="date"
            name="published_at"
            value={formData.published_at || ""}
            onChange={handleInputChange}
            placeholder="公開日"
            className="input-date"
            data-label="公開日"
            data-required="true"
          />

          <div className="input3 img-form">
            <MediaSelector
              label="記事画像"
              aspectRatio="16:9"
              type="image"
              fieldName="image"
              selectedMedia={formData.image}
              onSelect={handleImageSelect}
            />
          </div>
        </div>

        <div className="button-group">
          <div className="toggle-section">
            <label>
              <input
                className="custom-checkbox-input"
                type="checkbox"
                name="is_published"
                checked={formData.is_published}
                onChange={handleInputChange}
              />
              <div
                className={`custom-checkbox ${
                  formData.is_published ? "checked" : ""
                }`}
              >
                <span>{formData.is_published ? "公開" : "非公開"}</span>
              </div>
            </label>
          </div>
          <div className="action-section">
            {!isNew && formData.id && (
              <button
                type="button"
                className="delete-btn"
                onClick={() => onDelete?.(formData.id)}
                title="記事を削除"
              >
                <i className="fa-regular fa-trash"></i>
              </button>
            )}
          </div>
        </div>

        <div className="input2 rich-editor-container">
          {/* 画像編集メニューのみを非表示 + エディタ最低高さ設定 + プロジェクト統一デザイン */}
          <style>{`
            /* rich-editor-containerのプロジェクト統一スタイル */
            .rich-editor-container {
              background-color: #ffffff35;
              border: 1px solid #ffffff50;
              border-radius: 4px;
              box-sizing: border-box;
              transition: border-color 0.3s ease;
            }
            .rich-editor-container:hover {
              border-color: #fff;
            }
            .rich-editor-container:focus-within {
              border-color: #fff;
            }

            /* SunEditor内部のスタイル調整 */
            .rich-editor-container .sun-editor {
              border: none;
              border-radius: 4px;
              overflow: hidden;
              min-height: 350px !important;
            }
            .rich-editor-container .se-wrapper {
              border: none;
              min-height: 350px !important;
            }
            .rich-editor-container .se-wrapper-inner {
              min-height: 300px !important;
            }
            .rich-editor-container .se-wrapper-wysiwyg {
              min-height: 300px !important;
              background-color: rgba(255, 255, 255, 0.05);
            }

            /* ツールバーのスタイル調整 */
            .rich-editor-container .se-toolbar {
              background-color: rgba(255, 255, 255, 0.1);
              border-bottom: 1px solid rgba(255, 255, 255, 0.2);
            }
            .rich-editor-container .se-btn {
              color: #fff;
              background-color: transparent;
              border: 1px solid rgba(255, 255, 255, 0.2);
              margin: 2px;
              border-radius: 3px;
            }
            .rich-editor-container .se-btn:hover {
              background-color: rgba(255, 255, 255, 0.1);
              border-color: #fff;
            }
            .rich-editor-container .se-btn.se-on {
              background-color: var(--active_1);
              border-color: var(--active_1);
            }

            /* 編集エリアのテキストカラー */
            .rich-editor-container .se-wrapper-wysiwyg {
              color: #fff;
            }

            /* 画像コンテナ内の編集コントローラーのみを非表示 */
            .se-image-container .se-controller,
            .se-image-container .se-image-controller,
            .se-image-container .se-resizing-bar,
            .se-image-container .__se__float-menu__,
            .se-image-container .se-image-size,
            .se-image-container .se-image-align,
            .se-image-container .se-image-caption,
            .se-image-container .se-image-link,
            .se-image-container .se-image-alt,
            .se-image-container .se-image-rotation {
              display: none !important;
            }
            /* 画像ホバー時のアウトラインを非表示 */
            .se-image-container figure:hover {
              outline: none !important;
              border: none !important;
            }
            /* 画像選択時のコントローラーを非表示 */
            .se-component.se-image-container:hover .se-controller {
              display: none !important;
            }
          `}</style>
          <SunEditor
            getSunEditorInstance={(sunEditor) => {
              if (sunEditor && typeof sunEditor === "object") {
                setEditorInstance(sunEditor);

                // エディタインスタンス取得後、現在のformDataにcontentがあれば設定
                setTimeout(() => {
                  if (
                    formData.content &&
                    !isInitializing &&
                    !isInsertingImage
                  ) {
                    if (typeof sunEditor.setContents === "function") {
                      sunEditor.setContents(formData.content);
                    } else if (typeof sunEditor.setHTML === "function") {
                      sunEditor.setHTML(formData.content);
                    }
                  }
                }, 100);
              }
            }}
            ref={editorRef}
            defaultValue={formData.content || ""}
            onChange={(content) => {
              // 画像挿入中や初期化中は onChange を無視
              if (isInsertingImage || isInitializing) {
                console.log(
                  "Ignoring onChange during image insertion or initialization",
                  {
                    isInsertingImage,
                    isInitializing,
                  }
                );
                return;
              }

              // コンテンツが実際に変更されているかチェック
              if (content === formData.content) {
                return;
              }

              const syntheticEvent = {
                target: { name: "content", value: content },
              };
              handleInputChange(syntheticEvent);
            }}
            onInput={(event) => {
              // リアルタイムでiframeに反映（入力中の即座な反映）
              if (!isInitializing && !isInsertingImage) {
                const editorInst =
                  editorInstance ||
                  (editorRef.current?.getInstance
                    ? editorRef.current.getInstance()
                    : null);
                if (editorInst) {
                  const content = editorInst.getContents
                    ? editorInst.getContents()
                    : "";
                  const cleanedForIframe = cleanHtmlContent(content);

                  // デバウンス処理でパフォーマンス向上
                  clearTimeout(window.inputDebounceTimer);
                  window.inputDebounceTimer = setTimeout(() => {
                    sendUpdateToIframe(
                      MESSAGE_TYPES.TEXT_UPDATE,
                      "content",
                      cleanedForIframe,
                      "newsContent"
                    );
                  }, 200); // 200msのデバウンス
                }
              }
            }}
            onKeyUp={(event) => {
              // キー入力時の即座な反映
              if (!isInitializing && !isInsertingImage) {
                const editorInst =
                  editorInstance ||
                  (editorRef.current?.getInstance
                    ? editorRef.current.getInstance()
                    : null);
                if (editorInst) {
                  const content = editorInst.getContents
                    ? editorInst.getContents()
                    : "";
                  const cleanedForIframe = cleanHtmlContent(content);

                  // デバウンス処理でパフォーマンス向上
                  clearTimeout(window.keyupDebounceTimer);
                  window.keyupDebounceTimer = setTimeout(() => {
                    sendUpdateToIframe(
                      MESSAGE_TYPES.TEXT_UPDATE,
                      "content",
                      cleanedForIframe,
                      "newsContent"
                    );
                  }, 300); // 300msのデバウンス
                }
              }
            }}
            onBlur={(event) => {
              const editorInst =
                editorInstance ||
                (editorRef.current?.getInstance
                  ? editorRef.current.getInstance()
                  : null);
              if (editorInst) {
                const content = editorInst.getContents
                  ? editorInst.getContents()
                  : "";

                if (!isInitializing && !isInsertingImage) {
                  const cleanedForIframe = cleanHtmlContent(content);
                  sendUpdateToIframe(
                    MESSAGE_TYPES.TEXT_UPDATE,
                    "content",
                    cleanedForIframe,
                    "newsContent"
                  );
                } else {
                  console.log(
                    "Skipping onBlur due to initialization or image insertion"
                  );
                }
              }
            }}
            onLoad={() => {
              // フォールバック：refからエディタインスタンスを取得
              setTimeout(() => {
                if (editorRef.current && !editorInstance) {
                  let editor = null;
                  if (typeof editorRef.current.getInstance === "function") {
                    editor = editorRef.current.getInstance();
                  }
                  if (editor) {
                    setEditorInstance(editor);
                  }
                }
              }, 300);
            }}
            setOptions={{
              width: "100%",
              imageHeightShow: false,
              imageWidthShow: false,
              imageSizeOnlyPercentage: true,
              height: 400,
              minHeight: 300, // 最低高さを設定
              buttonList: [
                ["undo", "redo"],
                ["fontSize"],
                [
                  "bold",
                  "italic",
                  "underline",
                  "fontColor",
                  "hiliteColor",
                  "align",
                ],
              ],
              language: "ja",
              // リアルタイム更新のための設定
              callBackSave: function (contents) {
                console.log(
                  "SunEditor callBackSave triggered:",
                  contents.substring(0, 100)
                );
                // callBackSaveからもiframeに送信（クリーンアップして送信）
                if (!isInitializing && !isInsertingImage) {
                  console.log("Sending cleaned content via callBackSave");
                  const cleanedForIframe = cleanHtmlContent(contents);

                  // デバウンス処理
                  clearTimeout(window.saveDebounceTimer);
                  window.saveDebounceTimer = setTimeout(() => {
                    sendUpdateToIframe(
                      MESSAGE_TYPES.TEXT_UPDATE,
                      "content",
                      cleanedForIframe,
                      "newsContent"
                    );
                  }, 100); // 100msのデバウンス
                }
              },
            }}
            placeholder="記事の内容を入力してください..."
          />
        </div>
      </div>
    );
  }
);

// memoize to prevent unnecessary re-renders
const MemoizedNewsEditForm = React.memo(NewsEditForm);

export default function NewsEditPage() {
  return (
    <MediaProvider>
      <NewsEditPageContent />
    </MediaProvider>
  );
}
